// src/pages/SalesInsightsPage.jsx
import React, { useState, useRef } from "react";
import axios from "../config/axiosInstance";
import { AI } from "../config/apiConfig";
import {
  HiOutlineQuestionMarkCircle,
  HiOutlineChartBar,
  HiOutlineDocumentText,
  HiOutlineClock,
  HiOutlineRefresh,
  HiOutlineDownload,
} from "react-icons/hi";
import ReactApexChart from "react-apexcharts";
import html2pdf from "html2pdf.js";
import moment from "moment";
import "./SalesInsightsPage.css";
import { Spinner } from "flowbite-react";

// =====================
// Mapa de traduções de colunas
// =====================
const COLUMN_LABEL_MAP = {
  stageName: "Etapa",
  funnelName: "Funil",
  ownerName: "Vendedor",
  userName: "Usuário",
  teamName: "Time",
  totalValue: "Valor total",
  totalAmount: "Valor total",
  totalCount: "Quantidade",
  count: "Quantidade",
  qty: "Quantidade",
  value: "Valor",
  createdAt: "Criado em",
  updatedAt: "Atualizado em",
  closedAt: "Fechado em",
  dueDate: "Vencimento",
  status: "Status",
  "Stage dd": "ID do estágio",
  "Total deals": "Total de negócios",
  "Total deal value": "Valor Total dos negócios",
  "User id": "Id do usuário",
};

// Humaniza chaves: snake/camel → label PT-BR
function humanizeColumnLabel(key = "") {
  if (!key) return "";

  if (COLUMN_LABEL_MAP[key]) return COLUMN_LABEL_MAP[key];

  let label = key.replace(/_/g, " ");
  label = label.replace(/([a-z])([A-Z])/g, "$1 $2");
  label = label.toLowerCase();
  label = label.charAt(0).toUpperCase() + label.slice(1);

  return label;
}

// =====================
// Helpers numéricos
// =====================
function normalizeNumber(value) {
  if (value == null || value === "") return NaN;

  if (typeof value === "number") return value;

  // Trata "1147.00" ou "1.234,56"
  const str = String(value).trim();
  const normalized = str.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

function isProbablyNumeric(value) {
  if (value == null || value === "") return false;
  const n = normalizeNumber(value);
  return Number.isFinite(n);
}

// =====================
// Helpers de chart (React Apex)
// =====================
function buildChartConfig(rows = [], chart = {}) {
  if (!chart) return null;

  const { type, xField, yField } = chart;
  let { seriesField } = chart;

  if (!type || !xField || !yField || !Array.isArray(rows) || !rows.length) {
    return null;
  }

  // não tem gráfico pra "table"
  if (type === "table") return null;

  // =============== Sanear seriesField ===============
  // - se vier vazio, igual ao xField, ou virar quase "id de linha", ignora
  if (!seriesField || seriesField === xField) {
    seriesField = null;
  } else {
    const distinctSeries = Array.from(new Set(rows.map((r) => r[seriesField])));
    if (distinctSeries.length >= rows.length) {
      seriesField = null;
    }
  }

  // =============== PIE ===============
  if (type === "pie") {
    const labels = rows.map((r) => String(r[xField] ?? ""));
    const data = rows.map((r) => normalizeNumber(r[yField]));

    return {
      apexType: "pie",
      series: data,
      options: {
        labels,
        legend: { position: "bottom" },
        dataLabels: {
          enabled: true,
          formatter: (val) => String(val),
        },
        tooltip: {
          y: {
            formatter: (val) => String(val),
          },
        },
      },
    };
  }

  // =============== BAR / LINE ===============
  const apexType = type === "line" ? "line" : "bar";
  const isBar = apexType === "bar";

  // ---- Caso simples: sem seriesField válido → 1 série só ----
  if (!seriesField) {
    const categories = rows.map((r) => String(r[xField] ?? ""));
    const data = rows.map((r) => normalizeNumber(r[yField]));

    return {
      apexType,
      series: [
        {
          name: humanizeColumnLabel(yField),
          data,
        },
      ],
      options: {
        chart: {
          toolbar: { show: false },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -30,
            trim: true,
          },
        },
        dataLabels: {
          enabled: isBar, // barra: mostra valor dentro
          style: {
            fontSize: "11px",
          },
          formatter: (val) =>
            typeof val === "number"
              ? val.toLocaleString("pt-BR")
              : String(val ?? ""),
        },
        plotOptions: isBar
          ? {
              bar: {
                dataLabels: {
                  position: "center",
                },
              },
            }
          : undefined,
        stroke: {
          curve: "smooth",
          width: type === "line" ? 3 : 2,
        },
        legend: {
          position: "bottom",
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              typeof val === "number"
                ? val.toLocaleString("pt-BR")
                : String(val ?? ""),
          },
        },
        grid: {
          borderColor: "#e2e8f0",
        },
      },
    };
  }

  // ---- Caso avançado: seriesField válido → multi-série agregada ----
  const categoriesSet = new Set();
  const seriesMap = new Map(); // serieName -> Map(cat -> valor somado)

  rows.forEach((row) => {
    const cat = String(row[xField] ?? "");
    const serieName = String(row[seriesField] ?? "Série");
    const value = normalizeNumber(row[yField]);

    categoriesSet.add(cat);

    if (!seriesMap.has(serieName)) {
      seriesMap.set(serieName, new Map());
    }

    const catMap = seriesMap.get(serieName);
    catMap.set(cat, (catMap.get(cat) || 0) + value);
  });

  const categories = Array.from(categoriesSet);
  const series = Array.from(seriesMap.entries()).map(([name, catMap]) => ({
    name,
    data: categories.map((cat) => catMap.get(cat) || 0),
  }));

  return {
    apexType,
    series,
    options: {
      chart: {
        toolbar: { show: false },
      },
      xaxis: {
        categories,
        labels: {
          rotate: -30,
          trim: true,
        },
      },
      dataLabels: {
        enabled: isBar,
        style: {
          fontSize: "11px",
        },
        formatter: (val) =>
          typeof val === "number"
            ? val.toLocaleString("pt-BR")
            : String(val ?? ""),
      },
      plotOptions: isBar
        ? {
            bar: {
              dataLabels: {
                position: "center",
              },
            },
          }
        : undefined,
      stroke: {
        curve: "smooth",
        width: type === "line" ? 3 : 2,
      },
      legend: {
        position: "bottom",
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) =>
            typeof val === "number"
              ? val.toLocaleString("pt-BR")
              : String(val ?? ""),
        },
      },
      grid: {
        borderColor: "#e2e8f0",
      },
    },
  };
}

// =====================
// Component principal
// =====================
function SalesInsightsPage() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [hasAsked, setHasAsked] = useState(false);

  // histórico em memória da sessão
  const [history, setHistory] = useState([]);

  // ref da área do relatório (para PDF)
  const reportRef = useRef(null);

  const quickExamples = [
    "Quais funis geraram mais valor nos últimos 30 dias?",
    "Quais etapas têm mais negócios parados há mais de 14 dias?",
    "Quantas tarefas vencidas cada vendedor possui esta semana?",
    "Qual o valor ganho por vendedor no mês atual?",
  ];

  const insightState = loading
    ? {
        label: "O Estagiário está decodificando seus dados",
        detail:
          "Executando a consulta, separado métricas e preparando o resumo visual.",
        badge: "Processando",
        tone: "text-amber-200",
      }
    : result
    ? {
        label: "Insight pronto para revisão",
        detail: "Revise, exporte ou peça follow-ups no mesmo fluxo.",
        badge: "Pronto",
        tone: "text-lime-200",
      }
    : {
        label: "O Estagiário está disponível",
        detail: "Descreva o que quer analisar e ele gera tudo rapidamente.",
        badge: "Disponível",
        tone: "text-sky-200",
      };

  async function handleAsk(e) {
    e?.preventDefault?.();
    if (!question.trim()) return;

    setError("");
    setResult(null);
    setHasAsked(true);

    try {
      setLoading(true);
      const { data } = await axios.post(AI.INSIGHTS, { question });

      if (!data.ok) {
        setError(data.message || "Erro ao gerar insight.");
      } else {
        setResult(data);

        // adiciona ao histórico (apenas em memória)
        setHistory((prev) => {
          const newItem = {
            id: Date.now(),
            question: question.trim(),
            createdAt: new Date().toISOString(),
            chartType: data?.chart?.type || null,
            rowsCount: Array.isArray(data.rows) ? data.rows.length : 0,
          };
          return [newItem, ...prev].slice(0, 10); // limita últimos 10
        });
      }
    } catch (err) {
      console.error(err);
      setError("Erro ao falar com o servidor.");
    } finally {
      setLoading(false);
    }
  }

  const hasTable =
    result && Array.isArray(result.rows) && result.rows.length > 0;

  const hasChart =
    result &&
    result.chart &&
    hasTable &&
    result.chart.type &&
    result.chart.type !== "table";

  const chartConfig = hasChart
    ? buildChartConfig(result.rows, result.chart)
    : null;

  const truncatedQuestion = question
    ? question.length > 200
      ? `${question.slice(0, 197)}...`
      : question
    : "Sem pergunta ativa";

  const missions = history.slice(0, 3);

  // Detectar automaticamente quais colunas da tabela são numéricas
  const numericColumns = new Set();
  if (hasTable) {
    const sampleRows = result.rows.slice(0, 50); // pega mais linhas pra amostrar melhor
    const keys = Object.keys(result.rows[0] || {});

    keys.forEach((key) => {
      let allNumeric = true;
      let sawNumeric = false;

      for (const row of sampleRows) {
        const v = row[key];

        // ignora vazios na decisão
        if (v == null || v === "") continue;

        if (!isProbablyNumeric(v)) {
          allNumeric = false;
          break;
        }

        sawNumeric = true;
      }

      // só marca como numérica se:
      // - todo valor NÃO vazio foi numérico
      // - e pelo menos um valor numérico foi visto
      if (allNumeric && sawNumeric) {
        numericColumns.add(key);
      }
    });
  }

  function handleExampleClick(example) {
    setQuestion(example);
  }

  function handleHistoryClick(item) {
    setQuestion(item.question);
  }

  async function handleExportPdf() {
    if (!reportRef.current || !result) return;

    const element = reportRef.current;

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `insight-relatorio-${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: {
        mode: ["css", "avoid-all", "legacy"],
      },
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("Erro ao gerar PDF", err);
    }
  }

  return (
    <div className="insights-page">
      <section className="insights-hero">
        <div className="insights-hero-overlay" />
        <div className="insights-hero-grid">
          <div className="insights-hero-copy">
            <p className="hero-tagline">O Estagiário</p>
            <h1 className="hero-title">
              O assistente virtual que traduz dados em relatórios prontos.
            </h1>
            <p className="hero-lead">
              Descreva o que precisa monitorar, ele monta gráficos, destaca
              alertas e entrega um resumo claro.
            </p>
          </div>

          <div className="hero-visual">
            <img src="/assets/estagiario.png" alt="Estagiário" />
          </div>
        </div>
      </section>

      <div className="insights-layout">
        <main className="insights-main">
          <section className="input-panel">
            <div className="input-panel-header">
              <div>
                <p className="input-panel-subtitle">Pergunte com contexto</p>
                <h2 className="input-panel-title">O Estagiário está ouvindo</h2>
              </div>
              <span className="input-panel-badge">
                <HiOutlineQuestionMarkCircle
                  className="input-panel-icon"
                  aria-hidden="true"
                />
                {insightState.detail}
              </span>
            </div>
            <form onSubmit={handleAsk} className="input-form">
              <textarea
                rows={4}
                placeholder="Ex: Quais departamentos ultrapassaram a meta no funil de vendas regional nos últimos 30 dias?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="assistant-textarea"
              />
              <div className="input-footer">
                <p className="input-hint">
                  Dica rápida: mencione funil, período ou responsáveis para
                  afinar a análise.
                </p>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="button-loading flex items-center gap-2">
                      <Spinner />
                      <div>Gerando insight...</div>
                    </span>
                  ) : (
                    "Perguntar"
                  )}
                </button>
              </div>
            </form>
          </section>

          {error && <div className="alert alert-danger">{error}</div>}

          {!loading && !result && hasAsked && !error && (
            <div className="alert alert-info">
              Pedido recebido. Tente refinar sua pergunta com período, funil ou
              responsável caso nada apareça.
            </div>
          )}

          {loading && (
            <div className="insights-loader">
              <span className="assistant-spinner" />
              Gerando insight...
            </div>
          )}

          {result && (
            <section className="result-panel">
              <div className="result-summary">
                <div>
                  <p className="result-summary-text">
                    Pergunta:{" "}
                    <span className="result-summary-question">
                      {truncatedQuestion}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleExportPdf}
                  disabled={!result}
                >
                  <HiOutlineDownload
                    className="download-icon"
                    aria-hidden="true"
                  />
                  Exportar PDF
                </button>
              </div>

              <div ref={reportRef} className="report-card">
                <div className="report-grid">
                  <div className="report-box">
                    <p className="report-box-title">Explicação da IA</p>
                    <div
                      className="report-box-body"
                      dangerouslySetInnerHTML={{
                        __html:
                          result.summaryHtml ||
                          `<p>${
                            result.summary ||
                            "A IA gerou um insight, mas não retornou resumo."
                          }</p>`,
                      }}
                    />
                  </div>

                  <div className="report-box">
                    <div className="report-box-head">
                      <p className="report-box-title">Visual sugerido</p>
                      <span className="report-box-visual-type">
                        {hasChart
                          ? result.chart?.type || "Gráfico"
                          : hasTable
                          ? "Tabela"
                          : "Sem visual"}
                      </span>
                    </div>
                    {hasChart && chartConfig ? (
                      <div className="chart-frame">
                        <ReactApexChart
                          type={chartConfig.apexType}
                          series={chartConfig.series}
                          options={chartConfig.options}
                          height="100%"
                          width="100%"
                        />
                        <p className="chart-caption">
                          Campos:{" "}
                          <span className="chart-field">
                            {result.chart.xField} × {result.chart.yField}
                          </span>
                          {result.chart.seriesField && (
                            <>
                              {" · Série: "}
                              <span className="chart-field">
                                {result.chart.seriesField}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="report-box-note">
                        {hasTable
                          ? "Dados tabulares disponíveis abaixo."
                          : "Nenhum dado estruturado retornado para este insight."}
                      </p>
                    )}
                  </div>
                </div>

                {hasTable && (
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          {Object.keys(result.rows[0]).map((key) => (
                            <th
                              key={key}
                              className={
                                numericColumns.has(key)
                                  ? "align-right"
                                  : "align-left"
                              }
                            >
                              {humanizeColumnLabel(key)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row, idx) => (
                          <tr key={idx}>
                            {Object.keys(row).map((key) => {
                              const raw = row[key];
                              const isNumeric = numericColumns.has(key);
                              const display = isNumeric
                                ? normalizeNumber(raw)
                                : raw ?? "—";

                              return (
                                <td
                                  key={key}
                                  className={
                                    isNumeric ? "align-right" : "align-left"
                                  }
                                >
                                  {typeof display === "number"
                                    ? display.toLocaleString("pt-BR")
                                    : String(display)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}

          {!result && !error && !loading && !hasAsked && (
            <div className="empty-state">
              <p>Comece perguntando ao Estagiário</p>
              <p className="empty-state-note">
                Pergunte sobre valores por funil, performance por vendedor,
                tarefas atrasadas ou qualquer análise manual.
              </p>
            </div>
          )}
        </main>

        <aside className="insights-aside">
          <section className="examples-panel">
            <div className="panel-header">
              <h3>Exemplos rápidos</h3>
              <HiOutlineRefresh aria-hidden="true" />
            </div>
            <p className="panel-note">
              Clique para preencher a pergunta e ajuste conforme necessário.
            </p>
            <div className="examples-list">
              {quickExamples.slice(0, 4).map((ex, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="panel-button"
                  onClick={() => handleExampleClick(ex)}
                >
                  {ex}
                </button>
              ))}
            </div>
          </section>

          <section className="history-panel">
            <div className="panel-header">
              <div className="history-title flex items-center gap-2 items-center justify-between">
                <h3>Histórico recente</h3>
                <HiOutlineClock aria-hidden="true" />
              </div>
            </div>
            {history.length === 0 ? (
              <p className="panel-note">
                Cada pergunta aparece aqui para reutilizar depois.
              </p>
            ) : (
              <ul className="history-list">
                {history.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="history-item"
                      onClick={() => handleHistoryClick(item)}
                    >
                      <div className="history-row">
                        <p>{item.question}</p>
                        {item.chartType && (
                          <span className="history-badge">
                            {item.chartType}
                          </span>
                        )}
                      </div>
                      <span className="history-meta">
                        {item.rowsCount} linha(s) ·{" "}
                        {item.chartType || "sem gráfico"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

export default SalesInsightsPage;
